/**
 * The colour each address shows first, and its curtain.
 *
 * `ROUTE_THEMES` is what PageTransition paints: the closed curtain a page
 * loads behind (its `bg`), and the logo and counter drawn on it. The
 * carousel's entry is the entrance backdrop instead of a curtain, so its
 * `bg` is that backdrop's colour, and a curtain into the carousel is the
 * same colour so the two hand over unseen.
 *
 * `ROUTE_FIRST_PAINT` is the same colours, by address, for the frame the
 * browser paints before any script has run: index.html's head script
 * sets the address's colour on the root, so that frame already matches
 * the app's first one (the backdrop or the closed curtain) instead of
 * flashing the dark default under a light page on every load. The Vite
 * plugin in vite.config.ts inlines the map into index.html at dev and
 * build time, which is why this module is plain data with no browser
 * access: it is evaluated by the config too, outside the browser. (The
 * panel registry reads `window` at load, so panel paths are spelled out
 * here as registry.ts's getPanelPath does.)
 */
import { ROUTES } from "../../routes";
import { SLIDES } from "../Portfolio/constants";
import { animationConfig } from "../../config/animation";

export type RouteTheme = {
    bg: string;
    stroke: string;
    fill: string;
};

/** The theme key for any path that is not a page: the 404. */
export const NOT_FOUND = "*";

/** The site's dark surface (tailwind `surface-dark`). */
const DARK = "rgb(28,28,28)";

/** A panel's page background (the About panel, a case study): its explicit
 *  `bgColor`, else the base off-white washed with a faint amount of its
 *  text colour so each case study still has a subtly unique tone. The one
 *  formula for the panel itself (CaseStudyOverlay), the curtain it opens
 *  behind and its first paint, so none can drift from the others. */
export const panelBackground = (panel: { readonly bgColor?: string; readonly textColor?: string }) =>
    panel.bgColor ?? `color-mix(in srgb, ${panel.textColor ?? DARK} 2%, #FFFFFF)`;

/** Per-route curtain colors. Static routes are spelled out; case-study
 *  entries (`/<slug>`) are derived from SLIDES at module load so the
 *  curtain's stroke/fill stay in lockstep with each slide's textColor
 *  (same source of truth as Nav/Footer). Add another themed route here. */
export const ROUTE_THEMES: Record<string, RouteTheme> = {
    // The carousel plays its entrance rather than a curtain: the loader's
    // off-white backdrop, which a curtain into it must match.
    [ROUTES.index]: {
        bg: animationConfig.portfolio.entrance.backdrop.color,
        stroke: DARK,
        fill: DARK,
    },
    // Into the About panel from outside the carousel (the book, the 404):
    // the site's dark curtain, not the panel's white. The panel plays its
    // entrance once the curtain has lifted (CaseStudyOverlay's gate), and
    // on iOS sets the root colour to its own white then.
    [ROUTES.about]: {
        bg: DARK,
        stroke: "#FFFFFF",
        fill: "#FFFFFF",
    },
    // The 404, the avatar office (pages/NotFound): every path not listed.
    [NOT_FOUND]: {
        bg: "#f5f5f0",
        stroke: DARK,
        fill: DARK,
    },
    [ROUTES.book]: {
        bg: DARK,
        stroke: "#FFFFFF",
        fill: "#FFFFFF",
    },
    // Per-case-study entries derived from SLIDES. The curtain `bg` is
    // exactly what CaseStudyOverlay paints onto the page (panelBackground),
    // so the curtain doesn't snap to a different color than the page
    // underneath when it opens.
    ...Object.fromEntries(
        SLIDES
            .filter((s) => s.textColor)
            .map((s) => [
                `/${s.slug}`,
                {
                    bg: panelBackground(s),
                    stroke: s.textColor!,
                    fill: s.textColor!,
                } satisfies RouteTheme,
            ]),
    ),
};

/** Every known path has its own entry; anything else is the 404. */
export function resolveRouteKey(path: string): string {
    if (path in ROUTE_THEMES) return path;
    return NOT_FOUND;
}

export const getThemeForPath = (path: string): RouteTheme =>
    ROUTE_THEMES[resolveRouteKey(path)] ?? {
        bg: DARK,
        stroke: "#FFFFFF",
        fill: "#FFFFFF",
    };

/** What the browser's first frame should be, by address: the curtain the
 *  page loads behind, or the carousel's entrance backdrop. `"*"` covers
 *  every other address (the 404), as resolveRouteKey does. Inlined into
 *  index.html by vite.config.ts; its head script does the lookup. With
 *  reduced motion the carousel skips its entrance and is simply there on
 *  the dark page, so the script keeps the dark default for "/" then. */
export const ROUTE_FIRST_PAINT: Readonly<Record<string, string>> = Object.fromEntries(
    Object.entries(ROUTE_THEMES).map(([path, theme]) => [path, theme.bg]),
);
